import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import UserItem from "./UserItem";
import UserService from "../services/UserService";
import { AuthContext } from "../context/AuthContext";
import { useSnackbar } from "notistack";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import CircularProgress from "@material-ui/core/CircularProgress";
import AdminService from "../services/AdminService";
import InvitationGenerator from "./InvitationGenerator";
import getUserFriendlyMessages from "../genericFunctions/getUserFriendlyMessages";
import { CREATED, REMOVED } from "../genericFunctions/operationTypeConstants";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";
import FormControl from "@material-ui/core/FormControl";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
}));

const Users = () => {
  const classes = useStyles();
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const authContext = useContext(AuthContext);
  const [filterOfficeLocation, setFilterOfficeLocation] = useState("All");
  const [filterRole, setFilterRole] = useState("All");

  const rolesSortOrder = {
    externaluser: -4,
    user: -3,
    "admin/user": -2,
    admin: -1,
  };
  const updateFilter = (e) => {
    const capitalizeInput = e.target.value;
    setFilterOfficeLocation(capitalizeInput);
  };

  const onFilterRole = (e) => {
    setFilterRole(e.target.value);
  };

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [regKey, setRegKey] = useState(undefined);

  const generateRegistrationKey = () => {
    AdminService.generateRegisterKey().then((data) => {
      setRegKey(data.generatedRegistrationKey);
      setMessage(getUserFriendlyMessages(CREATED, "registration key"));
    });
  };

  const copyCodeToClipboard = () => {
    const elementToCopyFrom = document.getElementById("registrationUrl");
    navigator.clipboard.writeText(elementToCopyFrom.value);
    setMessage({
      msgBody: "Copied URL to clipboard",
    });
  };

  const getRegistrationUrl = () => {
    if (regKey === undefined) {
      return "No url is generated...";
    }
    const location = window.location.href.replace(
      "admin",
      `register/${regKey}`
    );
    return location;
  };

  const getUsers = () => {
    setLoading(true);
    UserService.getUsers().then((data) => {
      if (data && data.users) {
        setUsers(data.users);
        setLoading(false);
      } else {
        if (data && data.message) {
          setLoading(false);
          authContext.setIsAuthenticated(false);
          history.push("/login");
        }
      }
    });
  };

  useEffect(() => {
    if (
      authContext.user.role === "admin" ||
      authContext.user.role === "admin/user"
    ) {
      getUsers();
    } else {
      history.push("/account");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (message !== null) {
      enqueueSnackbar(message.msgBody, {
        variant: message.msgError ? "error" : "info",
      });
    }
    setTimeout(() => {
      setMessage(null);
    }, 3000);
  }, [message]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteUser = (user) => {
    if (
      authContext.user.role === "admin" ||
      authContext.user.role === "admin/user"
    ) {
      AdminService.deleteUser(user).then((data) => {
        const { message } = data;
        if (!message.msgError) {
          UserService.getUsers().then((data) => {
            setUsers(data.users);
            setMessage(getUserFriendlyMessages(REMOVED, "user"));
          });
        } else if (message.msgBody === "Unauthorized") {
          setMessage(message);
          authContext.setUser({ username: "", role: "" });
          authContext.setIsAuthenticated(false);
        } else {
          setMessage(message);
        }
      });
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <h3>Handle users</h3>
        <InvitationGenerator
          generateRegistrationKey={generateRegistrationKey}
          getRegistrationUrl={getRegistrationUrl}
          copyCodeToClipboard={copyCodeToClipboard}
        />
      </div>
      <div style={{ display: "flex" }}>
        <FormControl className={classes.formControl}>
          <Select
            name="siteLocation"
            value={filterOfficeLocation}
            onChange={updateFilter}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Göteborg">Göteborg</MenuItem>
            <MenuItem value="Linköping">Linköping</MenuItem>
          </Select>
          <FormHelperText>Site Location</FormHelperText>
        </FormControl>
        <FormControl className={classes.formControl}>
          <Select name="role" value={filterRole} onChange={onFilterRole}>
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="user">User</MenuItem>
            <MenuItem value="externaluser">External User</MenuItem>
          </Select>
          <FormHelperText>User Role</FormHelperText>
        </FormControl>
      </div>
      {loading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "65vh",
          }}
        >
          <CircularProgress color="secondary" />
        </div>
      )}
      {!loading && (
        <TableContainer>
          <Table style={{ minWidth: "650px" }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontSize: "20px", paddingLeft: "0px" }}>
                  Office Location
                </TableCell>
                <TableCell style={{ fontSize: "20px", paddingLeft: "0px" }}>
                  User
                </TableCell>
                <TableCell style={{ fontSize: "20px", paddingLeft: "0px" }}>
                  Role
                </TableCell>
                <TableCell style={{ fontSize: "20px", paddingLeft: "0px" }}>
                  Sales contact person
                </TableCell>
                <TableCell style={{ fontSize: "20px", paddingLeft: "0px" }}>
                  Availability
                </TableCell>
                <TableCell style={{ fontSize: "20px", paddingLeft: "0px" }}>
                  Status
                </TableCell>
                <TableCell
                  align="right"
                  style={{ paddingRight: "0px" }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users === undefined || users.length <= 0
                ? []
                : users
                    .sort(function (a, b) {
                      var x = rolesSortOrder[a.role];
                      var y = rolesSortOrder[b.role];
                      if (x < y) {
                        return -1;
                      }
                      if (x > y) {
                        return 1;
                      }
                      return 0;
                    })
                    .filter(
                      (consultant) =>
                        (filterOfficeLocation === "All"
                          ? true
                          : consultant.officeLocation
                          ? consultant.officeLocation.includes(
                              filterOfficeLocation
                            )
                          : false) &&
                        (filterRole === "All"
                          ? true
                          : consultant.role
                          ? consultant.role === filterRole
                          : false)
                    )
                    .map((consultant) => (
                      <UserItem
                        key={consultant._id}
                        userItem={consultant}
                        users={users}
                        deleteUser={handleDeleteUser}
                        setUsers={setUsers}
                        setMessage={setMessage}
                      />
                    ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default Users;
